import React, { createContext, useState, useContext, useEffect } from 'react';
import { NotificationContext } from './NotificationContext';
import { AUTH_TOKEN_STORAGE_KEY, BASE_URI } from './Config';

interface AuthContextProps {
    userId: string | null;
    userToken: string | null;
    userRole: string | null;
    signupSource: string | null;
    userVerified: boolean;
    userLineQuota: number | null;
    userEmail: string | null;
    loading: boolean;
    signIn: (email: string, password: string) => Promise<boolean>;
    signOut: () => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [userToken, setUserToken] = useState<string | null>(null);
    const [userId, setUserId] = useState<string | null>(null);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [signupSource, setSignupSource] = useState<string | null>(null);
    const [userVerified, setUserVerified] = useState(false);
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [userLineQuota, setUserLineQuota] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const notificationContext = useContext(NotificationContext);

    const clearSession = () => {
        setUserToken(null);
        setUserId(null);
        setUserRole(null);
        setSignupSource(null);
        setUserVerified(false);
        setUserEmail(null);
        setUserLineQuota(null);
        sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    };

    useEffect(() => {
        const token = sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
        if (token) {
            setUserToken(token);
            return;
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        let ignore = false;

        const checkUserRole = async () => {
            if (!userToken) {
                if (!ignore) {
                    setLoading(false);
                }
                return;
            }

            try {
                const response = await fetch(`${BASE_URI}/current_user_role`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${userToken}`
                    }
                });
                const data = await response.json();

                if (!response.ok || !data.user) {
                    clearSession();
                    return;
                }

                if (!ignore) {
                    setUserRole(data.user.role);
                    setSignupSource(data.user.signup_source ?? null);
                    setUserVerified(Boolean(data.user.verified));
                    setUserEmail(data.user.email);
                    setUserLineQuota(data.user.line_quota);
                    setUserId(data.user.id);
                }
            } catch (error) {
                if (!ignore) {
                    clearSession();
                    notificationContext?.pushMessage({
                        text: "Could not refresh your account session. Please sign in again.",
                        type: "error"
                    });
                }
            } finally {
                if (!ignore) {
                    setLoading(false);
                }
            }
        };

        setLoading(true);
        checkUserRole();

        return () => {
            ignore = true;
        };
    }, [notificationContext, userToken]);

    const signIn = async (email: string, password: string): Promise<boolean> => {
        const userData = new URLSearchParams({
            username: email,
            password,
        }).toString();

        try {
            setLoading(true);
            const response = await fetch(`${BASE_URI}/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: userData
            });
            const data = await response.json();

            if (response.ok && data.access_token) {
                sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, data.access_token);
                setUserToken(data.access_token);
                return true;
            }

            notificationContext?.pushMessage({ text: "Username or password incorrect", type: "error" });
        } catch (error) {
            notificationContext?.pushMessage({
                text: "There was a problem signing in. Please try again.",
                type: "error"
            });
        } finally {
            setLoading(false);
        }

        return false;
    };

    const signOut = () => {
        clearSession();
        setLoading(false);
    };

    return (
        <AuthContext.Provider value={{ userToken, loading, userRole, signupSource, userVerified, userEmail, userId, userLineQuota, signIn, signOut }}>
            {children}
        </AuthContext.Provider>
    );
};
